// ParcelRoleEditor — edits the ROSTER of people holding one EXPERT role on a parcel, for a value year.
//
// ⛔ SET REPLACEMENT, NOT APPEND. A role's holders for a year are the FULL SET of rows stamped with that
// year; resolution takes max(value_year) <= requested and returns ALL rows at that year. So editing the
// 2027 roster means writing every 2027 holder, including the ones carried over from 2021. If this UI
// inserted only the person being added, the incumbent would silently vanish from 2027 onward — the exact
// failure this component exists to prevent. It therefore loads the RESOLVED roster forward, edits it as a
// set, and writes the whole set.
//
// Prior years are never touched: the write only ever inserts rows stamped with the target year, and the
// database enforces that (cross-year isolation — a statement may touch at most one year).
//
// The picker DEFAULTS to people at the parcel's resolved firm, and can search client_contacts or another
// firm's contacts. Any person, any pool — the default is a convenience, not a restriction.
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { resolveRoleRoster } from '../utils/resolveAssignment';

export default function ParcelRoleEditor({ parcelId, role, roleLabel, valueYear, firmConsultantId, staffId, onClose, onSaved, s, c }) {
  const [people, setPeople]   = useState([]);      // the roster being edited: {contactId, pool, first_name, last_name, email}
  const [srcYear, setSrcYear] = useState(null);    // the year the roster was resolved FROM
  const [pool, setPool]       = useState('consultant');
  const [options, setOptions] = useState([]);
  const [q, setQ]             = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');

  const load = useCallback(async () => {
    try {
      const roster = await resolveRoleRoster(parcelId, valueYear);
      const cur = roster[role];
      setPeople(cur ? cur.people : []);
      setSrcYear(cur ? cur.year : null);
    } catch (e) { setErr(e.message || 'Could not load the roster.'); }
  }, [parcelId, role, valueYear]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      if (pool === 'consultant') {
        // Default to the parcel's assigned firm; with no firm resolved, offer every consultant contact
        // rather than an empty list — the person may be at a firm that isn't assigned here.
        let qy = supabase.from('consultant_contacts').select('id, first_name, last_name, email, consultant_id').order('last_name');
        if (firmConsultantId) qy = qy.eq('consultant_id', firmConsultantId);
        const { data } = await qy;
        setOptions(data || []);
      } else {
        const { data } = await supabase.from('client_contacts').select('id, first_name, last_name, email').order('last_name');
        setOptions(data || []);
      }
    })();
  }, [pool, firmConsultantId]);

  const inRoster = id => people.some(p => p.contactId === id);
  const add = o => setPeople(prev => inRoster(o.id) ? prev
    : [...prev, { contactId: o.id, pool, first_name: o.first_name, last_name: o.last_name, email: o.email }]);
  const remove = id => setPeople(prev => prev.filter(p => p.contactId !== id));

  async function save() {
    setBusy(true); setErr('');
    const y = Number(valueYear);
    // Replace this role's roster FOR THIS YEAR ONLY. Both statements are single-year, so cross-year
    // isolation passes and no other year can be affected even by accident.
    const { error: delErr } = await supabase.from('parcel_contact_roles')
      .delete().eq('parcel_id', parcelId).eq('role', role).eq('value_year', y);
    if (delErr) { setErr(delErr.message); setBusy(false); return; }

    if (people.length) {
      const rows = people.map(p => ({
        parcel_id: parcelId, role, value_year: y, created_by: staffId || null,
        client_contact_id:     p.pool === 'client'     ? p.contactId : null,
        consultant_contact_id: p.pool === 'consultant' ? p.contactId : null,
      }));
      const { error: insErr } = await supabase.from('parcel_contact_roles').insert(rows);
      if (insErr) { setErr(insErr.message); setBusy(false); return; }
    }
    setBusy(false);
    if (onSaved) onSaved();
    if (onClose) onClose();
  }

  const filtered = options.filter(o => {
    if (!q.trim()) return true;
    const t = `${o.first_name || ''} ${o.last_name || ''} ${o.email || ''}`.toLowerCase();
    return t.includes(q.trim().toLowerCase());
  }).slice(0, 40);

  return (
    <div style={{ padding: '12px', border: `1px solid ${c.border}`, borderRadius: '9px', marginTop: '8px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: c.text, marginBottom: '2px' }}>
        {roleLabel} — editing the {valueYear} roster
      </div>
      {/* Carried-forward rosters are the case people get wrong: say where these names came from. */}
      <div style={{ fontSize: '10px', color: c.muted, marginBottom: '8px', fontStyle: 'italic' }}>
        {srcYear == null ? 'No one assigned yet.'
          : srcYear === Number(valueYear) ? `Set for ${valueYear}.`
          : `Carried forward from ${srcYear}. Saving writes the whole list to ${valueYear}; ${srcYear} is unchanged.`}
      </div>
      {err && <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '6px' }}>{err}</div>}

      <div style={{ marginBottom: '8px' }}>
        {people.length === 0 && <div style={{ fontSize: '11px', color: c.muted }}>Nobody in this role.</div>}
        {people.map(p => (
          <div key={p.contactId} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: c.text, marginBottom: '3px' }}>
            <span style={{ flex: 1 }}>{`${p.first_name || ''} ${p.last_name || ''}`.trim() || '(no name)'}
              <span style={{ color: c.muted, fontSize: '10px' }}> · {p.pool === 'client' ? 'client' : 'consultant'}</span>
            </span>
            <button onClick={() => remove(p.contactId)} style={{ ...s.btnGhost, fontSize: '10px', padding: '2px 6px' }}>remove</button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
        <button onClick={() => setPool('consultant')} style={{ ...(pool === 'consultant' ? s.btn : s.btnGhost), fontSize: '10px', padding: '3px 8px' }}>Consultant</button>
        <button onClick={() => setPool('client')} style={{ ...(pool === 'client' ? s.btn : s.btnGhost), fontSize: '10px', padding: '3px 8px' }}>Client</button>
      </div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search people…" style={{ ...s.input, marginBottom: '6px' }} />
      <div style={{ maxHeight: '140px', overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: '6px', marginBottom: '8px' }}>
        {filtered.map(o => (
          <div key={o.id} onClick={() => add(o)}
            style={{ padding: '5px 8px', fontSize: '12px', cursor: 'pointer', color: inRoster(o.id) ? c.muted : c.text }}>
            {`${o.first_name || ''} ${o.last_name || ''}`.trim() || '(no name)'} {inRoster(o.id) && '✓'}
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: '6px 8px', fontSize: '11px', color: c.muted }}>No matches.</div>}
      </div>

      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={save} disabled={busy} style={{ ...s.btn, fontSize: '11px', padding: '4px 10px', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Saving…' : `Save ${valueYear} roster`}
        </button>
        <button onClick={onClose} style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px' }}>Cancel</button>
      </div>
    </div>
  );
}
